import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CODE_IMAGE_ABOUT } from '../../../../data/tool/tool-about.data';
import * as blinder from 'color-blind';
import { CodeImageTheme } from '../../../../data/theme/code-image-themes.data';
import { URLS } from '../../../../data/navigation/navigation.data';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { measureCodeImage, paintCodeImage } from '../../../../service/code-image/code-image.painter';
import { CODE_IMAGE_MAX_PIXELS, CodeImageState, codeImageOptions, codeImageTruncated, defaultCodeImageState } from '../../../../service/code-image/code-image.state';

/**
 * /tools/code-snippet - the code snippet image generator (2026-10-13). Paste code, pick a language and one
 * of the Codigrate themes, and the page draws it as a picture: the code in the theme's editor
 * colours on a rounded editor, over the theme's own gradient. The card is app-code-image-widget (also on the
 * home); this page draws the same settings at full size, through each colour vision deficiency and for the
 * download (code-image.state.ts). Nothing leaves the browser.
 */
@Component( {
  selector: 'app-code-image-page',
  templateUrl: './code-image-page.component.html',
  styleUrls: [
    './code-image-page.component.scss',
    '../wallpaper-page/wallpaper-page.component.scss',
    '../tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
  ],
} )
export class CodeImagePageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;
  SCALES = [ 1, 2, 3 ];

  /** the Preview section's picture: whole, as the download will be */
  @ViewChild( 'full' ) fullRef?: ElementRef<HTMLCanvasElement>;

  /** what the card is set to, as it last said */
  state: CodeImageState = defaultCodeImageState();
  /** the theme and the language the card opens on (?theme=sakura&lang=python) */
  themeKey = '';
  languageKey = '';
  exportScale = 2;

  /** the picture's size in CSS pixels, as last drawn */
  imageWidth = 0;
  imageHeight = 0;
  truncated = false;
  private paintQueued = false;

  pageSections: { key: string; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'vision', label: 'Color Vision' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'code-snippet';
  activeSection = 'top';
  readonly aboutLead = CODE_IMAGE_ABOUT.lead;
  readonly aboutParagraphs = CODE_IMAGE_ABOUT.paragraphs;
  readonly faq = CODE_IMAGE_ABOUT.faq;

  private sectionObserver?: IntersectionObserver;
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
  ) {
  }

  ngOnInit(): void {
    this.setSeo();

    // ?theme=sakura&lang=python opens the tool on that theme and language
    this.subs.push( this.route.queryParams.subscribe( q => {
      this.themeKey = q.theme || '';
      this.languageKey = q.lang || '';
    } ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
          }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    clearTimeout( this.cvdTimer );
    this.subs.forEach( s => s.unsubscribe() );
    this.sectionObserver?.disconnect();
  }

  // ---------------------------------------------------------------- the card

  onState( state: CodeImageState ): void {
    this.state = state;
    this.schedulePaint();
  }

  setScale( scale: number ): void {
    this.exportScale = scale;
  }

  /** the download's size in pixels */
  get exportSize(): string {
    const scale = this.safeScale( this.exportScale );
    return Math.round( this.imageWidth * scale ) + ' × ' + Math.round( this.imageHeight * scale ) + ' px';
  }

  /** a theme or a language picked on the card goes into the url */
  syncUrl( state: CodeImageState ): void {
    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams: { theme: state.theme.key, lang: state.language.key },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    } );
  }

  // ---------------------------------------------------------------- the picture

  /** a scale the browser can still make a canvas for */
  private safeScale( scale: number ): number {
    const area = this.imageWidth * this.imageHeight;
    while ( scale > 1 && area * scale * scale > CODE_IMAGE_MAX_PIXELS ) {
      scale -= 1;
    }
    return scale;
  }

  private schedulePaint(): void {
    if ( this.paintQueued || typeof requestAnimationFrame === 'undefined' ) {
      return;
    }
    this.paintQueued = true;
    requestAnimationFrame( () => {
      this.paintQueued = false;
      this.paint();
    } );
  }

  /** the Preview section's picture, at its own size, and the colour vision pictures a moment later */
  paint(): void {
    const full = this.fullRef?.nativeElement;
    const ctx = full?.getContext( '2d' );
    if ( !full || !ctx ) {
      return;
    }
    const own = measureCodeImage( ctx, codeImageOptions( this.state, 1 ) );
    // at most its own size (owner 2026-10-13: blown up to the section's width it lost its sharpness), at the
    // screen's pixel density
    let fullScale = Math.min( 3, Math.max( 1, Math.round( window.devicePixelRatio || 1 ) ) );
    while ( fullScale > 1 && own.width * own.height * fullScale * fullScale > CODE_IMAGE_MAX_PIXELS ) {
      fullScale -= 1;
    }
    full.style.maxWidth = own.width + 'px';
    paintCodeImage( full, codeImageOptions( this.state, fullScale ) );
    this.imageWidth = own.width;
    this.imageHeight = own.height;
    this.truncated = codeImageTruncated( this.state );
    this.scheduleCvd();
  }

  // ----- Color Vision (owner 2026-10-13, as on the gradient and wallpaper tools): the picture redrawn with every
  // colour of the theme seen through each deficiency -----

  /** each condition's version of the picture (the picture itself is the Preview section's) */
  cvdPictures: { group: string; items: { name: string; note: string; url: string }[] }[] = [];
  private cvdTimer: any = null;

  private readonly CVD_GROUPS: { group: string; items: { name: string; type: string; note: string }[] }[] = [
    { group: 'Protan (red)', items: [
      { name: 'Protanomaly', type: 'protanomaly', note: 'Shifted L-cone (long-wavelength) sensitivity. Reds desaturate and darken.' },
      { name: 'Protanopia', type: 'protanopia', note: 'No functional L-cones. Red and green hues are confused and reds appear dim.' },
    ] },
    { group: 'Deutan (green)', items: [
      { name: 'Deuteranomaly', type: 'deuteranomaly', note: 'Shifted M-cone (medium-wavelength) sensitivity. The most prevalent deficiency.' },
      { name: 'Deuteranopia', type: 'deuteranopia', note: 'No functional M-cones. Red and green hues are confused at normal luminance.' },
    ] },
    { group: 'Tritan (blue)', items: [
      { name: 'Tritanomaly', type: 'tritanomaly', note: 'Shifted S-cone (short-wavelength) sensitivity. Blue and green converge.' },
      { name: 'Tritanopia', type: 'tritanopia', note: 'No functional S-cones. Blue and yellow hues are confused.' },
    ] },
    { group: 'Monochromacy (no color)', items: [
      { name: 'Achromatomaly', type: 'achromatomaly', note: 'Reduced function across all three cone types, yielding a heavily desaturated image.' },
      { name: 'Achromatopsia', type: 'achromatopsia', note: 'No functional cones. Perception reduces to luminance, seen as grayscale.' },
    ] },
  ];

  /** the eight pictures are drawn a moment after the typing stops, not on every key */
  private scheduleCvd(): void {
    clearTimeout( this.cvdTimer );
    this.cvdTimer = setTimeout( () => this.zone.run( () => this.buildCvd() ), 400 );
  }

  /** the theme with every one of its colours seen through one condition */
  private simulate( theme: CodeImageTheme, type: string ): CodeImageTheme {
    const out: any = { ...theme };
    for ( const key of Object.keys( out ) ) {
      const value = out[ key ];
      if ( typeof value === 'string' && /^#[0-9a-f]{6}$/i.test( value ) ) {
        out[ key ] = ( blinder as any )[ type ]( value ).toUpperCase();
      }
    }
    return out as CodeImageTheme;
  }

  private buildCvd(): void {
    const canvas = document.createElement( 'canvas' );
    const draw = ( theme: CodeImageTheme ) => {
      try {
        paintCodeImage( canvas, { ...codeImageOptions( this.state, 1 ), theme } );
        return canvas.toDataURL( 'image/png' );
      } catch {
        return '';
      }
    };
    this.cvdPictures = this.CVD_GROUPS.map( group => ( {
      group: group.group,
      items: group.items.map( item => ( { name: item.name, note: item.note, url: draw( this.simulate( this.state.theme, item.type ) ) } ) ),
    } ) );
  }

  /** The picture at the download's scale, as a PNG. */
  private render(): Promise<Blob> {
    const canvas = document.createElement( 'canvas' );
    paintCodeImage( canvas, codeImageOptions( this.state, this.safeScale( this.exportScale ) ) );
    return new Promise<Blob>( ( resolve, reject ) => {
      canvas.toBlob( blob => blob ? resolve( blob ) : reject( new Error( 'The picture could not be made' ) ), 'image/png' );
    } );
  }

  download(): void {
    this.render().then( blob => {
      const url = URL.createObjectURL( blob );
      const link = document.createElement( 'a' );
      link.href = url;
      link.download = 'codigrate-code-' + this.state.theme.key + '.png';
      document.body.appendChild( link );
      link.click();
      link.remove();
      setTimeout( () => URL.revokeObjectURL( url ), 4000 );
      this.analyticsService.logEvent( 'Code Snippet Downloaded', { theme: this.state.theme.key, language: this.state.language.key, backdrop: this.state.backdrop, scale: this.exportScale } );
    } ).catch( () => undefined );
  }

  private setSeo(): void {
    this.seoService.setMetaData( {
      title: 'Free Code Snippet Image Generator - Code to PNG in 25 Themes | Codigrate',
      description: 'Free code snippet image generator by Codigrate. Paste your code, pick a language and one of 24 editor themes, and download a sharp PNG of your snippet for social posts, slides, docs and READMEs. Made in your browser, nothing uploaded.',
      keywords: [ 'code snippet image generator', 'code snippet generator', 'code image generator', 'code to image', 'code to png', 'code screenshot', 'code snippet image', 'beautiful code screenshots', 'syntax highlighting image', 'share code as image', 'codigrate' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  // ---------------------------------------------------------------- the sections

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.tweenScroll( scroller, 0 );
      return;
    }
    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    // clear the nav and the sticky chips' bar under it
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }
}
